import { useMemo, useRef, useState } from 'react';
import type { CaseView } from '@chemoflow/contracts';
import { BOARD_FILTERS, boardFilterKey, stateOf, type BoardFilter } from '@chemoflow/domain';
import { Icon } from '../components/Icon.js';
import { PatientWho, StatusPill, TRAIL_STEP, Trail, cls } from '../components/Card.js';
import { useCardMotion } from '../lib/motion.js';
import { useBoardData } from '../state/board.js';
import { useClock } from '../state/clock.js';

/** soft：完了の状態。点を白抜きにして、進行中（塗り）と見分ける。 */
const FILTER_LABEL: Record<BoardFilter | 'all', { label: string; color: string; soft?: boolean }> =
  {
    all: { label: 'すべて', color: 'var(--primary)' },
    idle: { label: '入力前', color: 'var(--idle)' },
    wait: { label: '調製待ち', color: 'var(--primary)' },
    start: { label: '調製開始済み', color: 'var(--start)' },
    made: { label: '調製完了', color: 'var(--start)', soft: true },
    admin: { label: '投与中', color: 'var(--admin)' },
    done: { label: '投与完了', color: 'var(--admin)', soft: true },
    stop: { label: '中止', color: 'var(--stop)' },
  };

const BOARD_STEPS = [
  TRAIL_STEP.clerk,
  TRAIL_STEP.pharm,
  TRAIL_STEP.pharmDone,
  TRAIL_STEP.admin,
  TRAIL_STEP.adminDone,
];

function BoardCard({ case: c, now }: { case: CaseView; now: number }) {
  const st = stateOf(c);
  return (
    <article
      className={`card compact ${st === 'stop' ? 'is-stopped' : ''}`.trim()}
      data-id={c.caseId}
    >
      <div className="row">
        <StatusPill case={c} now={now} />
        <PatientWho case={c} rp />
        {c.clerk?.reason ? (
          <div
            className={`note ${c.clerk.type === 'stop' ? 'is-stop' : ''}`.trim()}
            title={c.clerk.reason}
          >
            <p className="clamp2">{c.clerk.reason}</p>
          </div>
        ) : null}
        <Trail steps={BOARD_STEPS.map((step) => step(c))} />
      </div>
    </article>
  );
}

/** 全体ボード：いま、誰がどの状態か。絞り込みと氏名・患者IDの検索を持つ。 */
export function BoardView() {
  const { cases } = useBoardData();
  const { now } = useClock();
  const viewRef = useRef<HTMLElement>(null);
  const [filter, setFilter] = useState<BoardFilter | 'all'>('all');
  const [query, setQuery] = useState('');
  useCardMotion(viewRef, cases);

  const counts = useMemo(() => {
    const out = Object.fromEntries(BOARD_FILTERS.map((k) => [k, 0])) as Record<BoardFilter, number>;
    for (const c of cases) out[boardFilterKey(c)] += 1;
    return out;
  }, [cases]);

  const list = useMemo(() => {
    const q = query.trim().replace(/\s/g, '');
    const rank = (c: CaseView) => BOARD_FILTERS.indexOf(boardFilterKey(c));
    return cases
      .filter((c) => filter === 'all' || boardFilterKey(c) === filter)
      .filter((c) => {
        if (!q) return true;
        const p = c.patient;
        if (!p) return c.surrogateKey.includes(q);
        return p.name.replace(/\s/g, '').includes(q) || p.patientId.includes(q);
      })
      .sort((a, b) => rank(a) - rank(b) || a.rosterIndex - b.rosterIndex);
  }, [cases, filter, query]);

  const chip = (key: BoardFilter | 'all', count: number) => {
    const { label, color, soft } = FILTER_LABEL[key];
    return (
      <button
        key={key}
        className={cls('fchip', soft && 'is-soft')}
        type="button"
        aria-pressed={filter === key}
        style={{ '--c': color } as React.CSSProperties}
        onClick={() => setFilter(key)}
      >
        {key === 'all' ? null : <i />}
        {label}
        <em>{count}</em>
      </button>
    );
  };

  return (
    <main className="board is-one view" id="view-board" data-view="board" ref={viewRef}>
      <section>
        <div className="filters" id="filters">
          <label className="search">
            <Icon name="search" />
            <input
              id="board-q"
              type="search"
              placeholder="氏名・患者ID"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          {chip('all', cases.length)}
          {BOARD_FILTERS.map((k) => chip(k, counts[k]))}
        </div>
        <div className="list">
          {list.length ? (
            list.map((c) => <BoardCard key={c.caseId} case={c} now={now} />)
          ) : (
            <p className="empty">該当する患者はいません</p>
          )}
        </div>
      </section>
    </main>
  );
}
